import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Customer } from '../../classes/customer';
import { CustomerService } from '../../services/customer';
import { AuthService } from '../../services/auth';
import { User } from '../../classes/user';
import { Document } from '../../classes/document';
import { DocumentService } from '../../services/document';
import {
    CUSTOMER_DOCUMENT_TYPES,
    CUSTOMER_DOCUMENT_TYPE_LABELS,
    CUSTOMER_DOCUMENT_TYPE_NAMES,
    createEmptyCustomerDocumentList,
    mergeCustomerDocumentsIntoList
} from '../../utils/customerDocuments';
import { customerEmailAddresses, normalizeCustomerEmails } from '../../utils/customerEmails';

@Component({
    selector: 'app-customer-update',
    templateUrl: './customer-update.component.html',
    styleUrls: ['./customer-update.component.css']
})
export class CustomerUpdateComponent implements OnInit {
    user: User = new User();
    customer: Customer = new Customer();
    emailInputs: string[] = [''];
    emailError: string = '';
    loadingCondition: boolean = false;
    customerDocumentTypes = CUSTOMER_DOCUMENT_TYPES;
    customerDocumentTypeLabels = CUSTOMER_DOCUMENT_TYPE_LABELS;
    documentList: { [key: number]: Document[] } = createEmptyCustomerDocumentList();
    fileName: { [key: number]: string } = {};
    fileData: { [key: number]: FormData } = {};

    constructor(private route: ActivatedRoute,
                private router: Router,
                private authService: AuthService,
                private customerService: CustomerService,
                private documentService: DocumentService) {
        CUSTOMER_DOCUMENT_TYPES.forEach((id) => {
            this.fileName[id] = '';
            this.fileData[id] = new FormData();
        });
    }

    private formatDateInput(value: Date | string | null | undefined): string {
        if (!value) {
            return '';
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
            return value.trim();
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return '';
        }
        // Uso metodi UTC per evitare che il fuso orario sposti la data di un giorno (es. UTC-5)
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private parseDateInput(value: string): Date | null {
        if (!value) {
            return null;
        }
        // Parsing esplicito YYYY-MM-DD come data di calendario (mezzanotte UTC) per evitare sfasamenti
        const [y, m, d] = value.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, d));
    }

    get registrationDateString(): string {
        return this.formatDateInput(this.customer.RegistrationDate);
    }

    set registrationDateString(value: string) {
        this.customer.RegistrationDate = this.parseDateInput(value);
    }

    get visuraDateString(): string {
        return this.formatDateInput(this.customer.VisuraDate);
    }

    set visuraDateString(value: string) {
        this.customer.VisuraDate = this.parseDateInput(value);
    }

    get balanceDateString(): string {
        return this.formatDateInput(this.customer.BalanceDate);
    }

    set balanceDateString(value: string) {
        this.customer.BalanceDate = this.parseDateInput(value);
    }

    get birthDateString(): string {
        return this.formatDateInput(this.customer.BirthDate);
    }

    set birthDateString(value: string) {
        this.customer.BirthDate = this.parseDateInput(value);
    }

    ngOnInit(): void {
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                if (this.user.FKIDUserRole !== 1) {
                                    this.router.navigate(['/dashboard']);
                                    return;
                                }
                                this.loadCustomer();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private loadCustomer(): void {
        this.route.paramMap.subscribe(params => {
            this.customerService.getByID(params.get('id'))
                .subscribe({
                    next: (customer: Customer) => {
                        this.customer = customer;
                        const emails = customerEmailAddresses(customer);
                        this.emailInputs = emails.length ? emails : [''];
                        this.emailError = '';
                        this.loadDocuments();
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    private loadDocuments(): void {
        if (!this.customer.IDCustomer) {
            this.documentList = createEmptyCustomerDocumentList();
            return;
        }
        this.documentService.getAllByIDCustomer(this.customer.IDCustomer.toString())
            .subscribe({
                next: (documents: any) => {
                    mergeCustomerDocumentsIntoList(this.documentList, documents);
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    allowsMultipleFiles(idDocumentType: number): boolean {
        return idDocumentType === 4;
    }

    onPasteIBAN(event: any) {
        let ibanPasted = event.clipboardData.getData('text');
        ibanPasted = ibanPasted.replace(/ /g, '');
        ibanPasted = ibanPasted.substring(0, 27).toUpperCase();
        this.customer.IBAN = ibanPasted;
    }

    onChangeIBAN(event: any) {
        this.customer.IBAN = event.replace(/ /g, '').toUpperCase();
    }

    uploadDocument(event: any, idDocumentType: number) {
        this.loadingCondition = true;
        const files: Array<File> = event.target.files;
        if (!files || files.length === 0 || !this.customer.IDCustomer) {
            this.loadingCondition = false;
            return;
        }

        this.fileData[idDocumentType] = new FormData();
        for (let i = 0; i < files.length; i++) {
            this.fileData[idDocumentType].append('uploadFile', files[i]);
        }
        this.fileName[idDocumentType] = (files.length === 1) ? files[0].name : files.length + ' file selezionati';

        let documentIncrementalPart = '';
        if (idDocumentType === 4) {
            let maxDocumentIndex = 0;
            (this.documentList[idDocumentType] || []).forEach((currentDocument: Document) => {
                const currentDocumentPathParts = currentDocument.Path.split('---')[0].split('_');
                const currentDocumentIndex = parseInt(currentDocumentPathParts[currentDocumentPathParts.length - 1], 10);
                if (!isNaN(currentDocumentIndex) && currentDocumentIndex > maxDocumentIndex) {
                    maxDocumentIndex = currentDocumentIndex;
                }
            });
            documentIncrementalPart = '_' + (maxDocumentIndex + 1);
        }

        const firstFile = this.fileData[idDocumentType].getAll('uploadFile')[0] as File;
        const fileNameParts: Array<string> = (firstFile?.name || this.fileName[idDocumentType] || '').split('.');
        const extension = fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : '';

        this.fileData[idDocumentType].set('IDDocumentType', idDocumentType.toString());
        this.fileData[idDocumentType].set('IDCustomer', this.customer.IDCustomer.toString());
        this.fileData[idDocumentType].set(
            'FilePath',
            CUSTOMER_DOCUMENT_TYPE_NAMES[idDocumentType] + documentIncrementalPart + '---' +
            this.customer.VATNumber + '_' + this.customer.IDCustomer.toString() +
            (extension ? '.' + extension : '')
        );

        this.documentService.create(this.fileData[idDocumentType])
            .subscribe({
                next: (documentsCreated: any) => {
                    if (!this.documentList[idDocumentType]) {
                        this.documentList[idDocumentType] = [];
                    }
                    if (Array.isArray(documentsCreated)) {
                        this.documentList[idDocumentType].push(...documentsCreated);
                    } else if (documentsCreated) {
                        this.documentList[idDocumentType].push(documentsCreated);
                    }
                    this.fileData[idDocumentType] = new FormData();
                    this.fileName[idDocumentType] = '';
                    this.loadingCondition = false;
                    // Ricarica i master per allinearsi al replace-on-upload lato back-end
                    this.loadDocuments();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                    this.loadingCondition = false;
                }
            });
    }

    deleteDocument(document: Document) {
        this.loadingCondition = true;
        this.documentService.deleteCustomerMaster(document.IDDocument)
            .subscribe({
                next: () => {
                    const typeId = document.FKIDDocumentType!;
                    this.documentList[typeId] = (this.documentList[typeId] || []).filter(
                        (currentDocument: Document) => document.IDDocument !== currentDocument.IDDocument
                    );
                    this.loadingCondition = false;
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                    this.loadingCondition = false;
                }
            });
    }

    saveAndGoToDetail() {
        const parsed = normalizeCustomerEmails(this.emailInputs);
        if (parsed.error) {
            this.emailError = parsed.error;
            return;
        }
        this.emailError = '';
        this.loadingCondition = true;
        this.customer.Email = '';
        this.customer.emails = parsed.emails.map(email => ({ Email: email }));
        this.customerService.update(this.customer)
            .subscribe({
                next: () => {
                    this.loadingCondition = false;
                    this.router.navigate(['/clienti/' + this.customer.IDCustomer]);
                },
                error: (err: HttpErrorResponse) => {
                    this.loadingCondition = false;
                    console.log(err);
                }
            });
    }

}
